import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage, FormattedNumber } from 'react-intl';
import Field from 'redux-form/es/Field';
import { pushGaStepEvent } from 'actions/loan';
import cx from 'classnames';
import Slider from 'components/Credit24/LoanApplication/OfferDetails/Slider';
import AccordionTooltip from 'components/common/controls/AccordionTooltip';
import InputSelectField from 'components/Credit24/Core/Controls/InputSelectField';
import { fromCents, getCurrencyCode } from 'utils/helpers';
import frequencyPeriods from 'consts/loan/frequencyPeriods';
import { useTranslationSuffix } from 'hooks/useTranslationSuffix';

const propTypes = {
  loan: PropTypes.object.isRequired,
  principalIndex: PropTypes.number,
  firstDrawAmountIndex: PropTypes.number,
  creditLimits: PropTypes.array,
  pushGaStepEvent: PropTypes.func,
};

const OfferDetailsAU = (props) => {
  const {
    loan,
    principalIndex = Number.MAX_SAFE_INTEGER,
    firstDrawAmountIndex = Number.MAX_SAFE_INTEGER,
    creditLimits,
  } = props;
  const {
    productType,
    availableProducts,
    availablePrincipalsIndex = [],
    paymentInterval,
  } = loan;

  useEffect(() => {
    props.pushGaStepEvent('product_offer');
  }, []);

  const isProductTypeInstallment = productType === 'INSTALLMENT';
  let isSubstituteInstallmentLoan = false;
  if (!PRODUCT_IL && !INCREASE_APP) {
    isSubstituteInstallmentLoan = isProductTypeInstallment;
  }
  let headerTitleKey = `offer.title.${productType}`;
  if (isSubstituteInstallmentLoan) {
    headerTitleKey = 'offer.title.substitute.INSTALLMENT';
  }

  const translationKeys = {
    headerTitle: headerTitleKey,
    substituteSubtitle: 'offer.subtitle.substitute.INSTALLMENT',
    maximum: `offer.maximum.${productType}`,
    principalTitle: `offer.${productType}.principal.title`,
    firstDrawAmountTitle: `offer.${productType}.firstDrawAmount.title`,
    mmpLabel: `offer.${productType}.mmp.label.${loan.paymentInterval.toLowerCase()}`,
    mmpMinimumRepay: 'offer.mmp.minimumRepay',
  };

  const translations = useTranslationSuffix('upgrade', translationKeys);

  const stepClasses = cx({
    'loan-form__step txt-center': true,
    'loan-form__step--with-reverse-headers': isSubstituteInstallmentLoan,
  });

  const maxPrincipalIndex = availablePrincipalsIndex.length - 1;
  const productPrincipalIndex = Math.min(principalIndex, maxPrincipalIndex);
  const product = availableProducts[availablePrincipalsIndex[productPrincipalIndex]];

  const totalBalanceRepaymentPeriodInMonths = product.totalBalanceRepaymentPeriodInMonths;

  const { firstDrawAmountIndexes } = product;
  let secondaryProducts = product.withdraws;

  let theme = null;
  if (isProductTypeInstallment) {
    secondaryProducts = product.productOffers;
    theme = 'maturity';
  }
  const maxFirstdrawAmountIndex = firstDrawAmountIndexes.length - 1;
  const firstDrawIndex = Math.min(firstDrawAmountIndex, maxFirstdrawAmountIndex);
  const {
    apr,
    minimumPayment,
    monthlyFee,
    establishmentFee,
    annualInterest,
    isAffordable,
  } = secondaryProducts[firstDrawAmountIndexes[firstDrawIndex]];

  const isValueSelected = PRODUCT_IL || isSubstituteInstallmentLoan || principalIndex >= 0;
  const offerDetailsClasses = cx({
    'offer-details': true,
    'offer-details--au-cl': !isProductTypeInstallment,
  });
  const offerDetailsItemClasses = cx({
    'offer-details__item': true,
    'offer-details__item--large': isValueSelected,
  });
  const principalTitle = (
    <h3 className="txt-left txt-muted">
      <FormattedMessage id={translations.principalTitle} />
    </h3>
  );

  const invoicingFeeKey = `offer.details.pricing.invoicingFee.${paymentInterval ?? frequencyPeriods.MONTHLY.type}`;
  const showHintMessage = !isAffordable && !INCREASE_APP;

  return (
    <div className={stepClasses}>
      <h1 className="loan-form__step-title txt-center">
        <FormattedMessage id={translations.headerTitle} />
      </h1>
      {isSubstituteInstallmentLoan && (
        <div className="loan-form__step-message">
          <div className="message message--callout">
            <span className="message__icon" />
            <div className="message__content txt-large">
              <FormattedHTMLMessage id={translations.substituteSubtitle} />
            </div>
          </div>
        </div>
      )}
      <div className="loan-form__step-body">
        <div className="grid grid--center">
          <div className="grid__item one-whole">
            {!isSubstituteInstallmentLoan && (
              <div className="message message--offer">
                <div className="message__content">
                  <FormattedMessage id={translations.maximum} />
                </div>
                <div className="message__value">
                  <FormattedNumber
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                    currency={getCurrencyCode()}
                    style="currency"
                    value={fromCents(availablePrincipalsIndex[maxPrincipalIndex])}
                  />
                </div>
              </div>
            )}
            {showHintMessage
              && (
              <p className="offer-details__hint">
                <FormattedMessage id="offer.details.hint" />
              </p>
              )}
            <div className={offerDetailsClasses}>
              <div className={offerDetailsItemClasses}>
                {isProductTypeInstallment && (
                  <>
                    {principalTitle}
                    <Slider
                      name="principalIndex"
                      maxValue={maxPrincipalIndex}
                      minValue={0}
                      step={1}
                      defaultValue={productPrincipalIndex}
                      availablePrincipalsIndex={availablePrincipalsIndex}
                      productType={`${productType}_principal`}
                    />
                    <br />
                  </>
                )}
                <h3 className="txt-left txt-muted">
                  <FormattedMessage id={translations.firstDrawAmountTitle} />
                </h3>
                <Slider
                  name="firstDrawAmountIndex"
                  maxValue={maxFirstdrawAmountIndex}
                  minValue={0}
                  step={1}
                  defaultValue={firstDrawIndex}
                  availablePrincipalsIndex={firstDrawAmountIndexes}
                  productType={`${productType}_firstDrawAmount`}
                  theme={theme}
                />
                {!isProductTypeInstallment && (
                  <>
                    <br />
                    {principalTitle}
                    <Field
                      emptyTextKey="offer.CREDIT_LINE.credit_limit_title"
                      name="principalIndex"
                      component={InputSelectField}
                      options={creditLimits}
                      isFullWidth
                    />
                  </>
                )}
              </div>
              {isValueSelected
                && (
                  <div className="offer-details__item offer-details__item--small">
                    <div className="offer-details__amount">
                      <FormattedNumber
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                        currency={getCurrencyCode()}
                        style="currency"
                        value={fromCents(minimumPayment)}
                      />
                    </div>
                    <div className="offer-details__label txt-muted">
                      <FormattedMessage
                        tagName="p"
                        id={translations.mmpLabel}
                      />

                      {productType === 'CREDIT_LINE'
                        && (
                          <FormattedHTMLMessage
                            id={translations.mmpMinimumRepay}
                            values={{
                              amount: totalBalanceRepaymentPeriodInMonths,
                            }}
                            tagName="p"
                          />
                        )}

                    </div>
                    {!isProductTypeInstallment && (
                      <div className="offer-details__cl-info">
                        <AccordionTooltip
                          title={<FormattedMessage id="offer.CREDIT_LINE.info.title" />}
                        >
                          <FormattedMessage id="offer.CREDIT_LINE.info.body" />
                        </AccordionTooltip>
                      </div>
                    )}
                  </div>
                )}
            </div>
            <FormattedMessage id="offer.details.pricing.title" tagName="h3" />
            <table className="table table--offer-page-simple">
              <tbody>
                <tr>
                  <td>
                    <FormattedMessage id={invoicingFeeKey} />
                  </td>
                  <td>
                    <FormattedNumber
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      currency={getCurrencyCode()}
                      style="currency"
                      value={fromCents(isProductTypeInstallment ? monthlyFee : product.monthlyFee)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage
                      id={`offer.details.pricing.${isProductTypeInstallment ? 'establishmentFee' : 'activationFee'}`}
                    />
                  </td>
                  <td>
                    <FormattedNumber
                      minimumFractionDigits={0}
                      maximumFractionDigits={2}
                      currency={getCurrencyCode()}
                      style="currency"
                      value={fromCents(
                        isProductTypeInstallment ? establishmentFee : product.activationFee
                      )}
                    />
                  </td>
                </tr>
                {(productType === 'CREDIT_LINE' || product.loanAmount > 200000) && (
                <tr>
                  <td>
                    <FormattedMessage id="offer.details.pricing.interestRate" />
                  </td>
                  <td>
                    <FormattedMessage
                      id="offer.details.pricing.interestRate.value.annual"
                      values={{
                        interest: <FormattedNumber
                          minimumFractionDigits={0}
                          maximumFractionDigits={2}
                          style="percent"
                          value={
                            // eslint-disable-next-line max-len
                            (isProductTypeInstallment ? annualInterest : product.annualInterest) / 100
                          }
                        />,
                      }}
                    />
                  </td>
                </tr>
                )}
                {(!isProductTypeInstallment || product.loanAmount > 200000) && (
                <tr>
                  <td>
                    <FormattedMessage id="offer.details.pricing.apr" />
                  </td>
                  <td>
                    <FormattedMessage
                      id="offer.details.pricing.interestRate.value.annual"
                      values={{
                        interest: <FormattedNumber
                          minimumFractionDigits={0}
                          maximumFractionDigits={2}
                          style="percent"
                          value={
                            (isProductTypeInstallment ? apr : product.apr) / 100
                          }
                        />,
                      }}
                    />
                  </td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

OfferDetailsAU.propTypes = propTypes;

export default connect(null, { pushGaStepEvent })(OfferDetailsAU);
